import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormRule } from 'antd';
import { Checkbox, Divider, Form, Input, InputNumber, Select, Space } from 'antd';
import { useGetCountries } from 'api/countries/use-get-countries.ts';
import { useRoleGuard } from 'shared/hooks/use-role-guard';
import { transformCountriesForForm } from 'shared/utils/countries';
import { ClientPaymentTerms, ClientType } from 'types/client.ts';
import { UserRole } from 'types/user.ts';
import type { IClientFormFields, IClientFormProps } from 'components/clients/client-form/types';
import { InputWithSearchSimilar } from './components/input-with-search-similar';

const validationRule: Record<keyof IClientFormFields, FormRule[]> = {
  shortName: [{ required: false, type: 'string', min: 3 }],
  name: [{ required: true, type: 'string', min: 3 }],
  type: [{ required: true, enum: Object.values(ClientType) }],
  paymentTerms: [{ required: true, type: 'string', enum: Object.values(ClientPaymentTerms) }],
  paymentPeriod: [{ required: true, type: 'integer' }],
  vatNumber: [{ required: true, type: 'string' }],
  legalPostCode: [{ required: true, type: 'string' }],
  legalCountryId: [{ required: true, type: 'number' }],
  legalCity: [{ required: true, type: 'string' }],
  legalAddress: [{ required: true, type: 'string' }],
  postPostCode: [{ required: false, type: 'string' }],
  postCountryId: [{ required: false, type: 'number' }],
  postCity: [{ required: false, type: 'string' }],
  postAddress: [{ required: false, type: 'string' }],
  emailForDocuments: [{ required: false, type: 'email' }],
  description: [{ required: false, type: 'string' }],
  isVip: [{ required: false, type: 'boolean' }],
  factoringEnabled: [{ required: false, type: 'boolean' }],
};

export const ClientForm: React.FC<IClientFormProps> = (props) => {
  const { form, formId, onFinish, initialValues, isCreatingMode } = props;

  const { t } = useTranslation('client');
  const isAdmin = useRoleGuard([UserRole.admin]);
  const { data: countries } = useGetCountries();

  const transformedCountries = useMemo(() => transformCountriesForForm(countries), [countries]);

  return (
    <Form form={form} id={formId} layout='vertical' initialValues={initialValues} onFinish={onFinish}>
      <InputWithSearchSimilar
        form={form}
        name='name'
        label={t('form_name')}
        placeholder={t('form_name')}
        validationRule={validationRule.name}
        withFindingSimilar={isCreatingMode}
      />

      <Form.Item name='shortName' label={t('form_short_name')} rules={validationRule.shortName}>
        <Input placeholder={t('form_short_name')} />
      </Form.Item>
      <Form.Item name='type' label={t('form_type')} rules={validationRule.type}>
        <Select placeholder={t('form_type')}>
          <Select.Option value={ClientType.direct}>{t('type_direct')}</Select.Option>
          <Select.Option value={ClientType.expedition}>{t('type_expedition')}</Select.Option>
          <Select.Option value={ClientType.carrier}>{t('type_carrier')}</Select.Option>
        </Select>
      </Form.Item>
      {isAdmin && (
        <Space size='large'>
          <Form.Item name='isVip' valuePropName='checked' rules={validationRule.isVip}>
            <Checkbox>{t('type_vip')}</Checkbox>
          </Form.Item>
          <Form.Item name='factoringEnabled' valuePropName='checked' rules={validationRule.factoringEnabled}>
            <Checkbox>{t('factoring_enabled')}</Checkbox>
          </Form.Item>
        </Space>
      )}

      <Divider orientation='left' orientationMargin='0'>
        {t('form_payment_info')}
      </Divider>
      <Space.Compact block>
        <Form.Item name='paymentTerms' style={{ width: '30%' }} rules={validationRule.paymentTerms}>
          <Select placeholder={t('form_payment_terms')}>
            <Select.Option value={ClientPaymentTerms.scan}>{t('payment_terms_scan')}</Select.Option>
            <Select.Option value={ClientPaymentTerms.original}>{t('payment_terms_original')}</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name='paymentPeriod' style={{ width: '70%' }} rules={validationRule.paymentPeriod}>
          <InputNumber
            placeholder={t('form_payment_period')}
            decimalSeparator={','}
            type={'number'}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Space.Compact>

      <Divider orientation='left' orientationMargin='0'>
        {t('form_legal_info')}
      </Divider>
      <InputWithSearchSimilar
        form={form}
        name='vatNumber'
        label={t('common:vat')}
        placeholder={t('common:vat')}
        validationRule={validationRule.vatNumber}
        withFindingSimilar={isCreatingMode}
      />
      <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
        <Form.Item name='legalCountryId' style={{ width: '34%' }} rules={validationRule.legalCountryId}>
          <Select
            optionFilterProp='label'
            placeholder={t('common:country')}
            options={transformedCountries}
            showSearch
          />
        </Form.Item>
        <Form.Item name='legalPostCode' rules={validationRule.legalPostCode} style={{ width: '33%' }}>
          <Input placeholder={t('common:post_code')} />
        </Form.Item>
        <Form.Item name='legalCity' rules={validationRule.legalCity} style={{ width: '33%' }}>
          <Input placeholder={t('common:city')} />
        </Form.Item>
      </div>
      <Form.Item name='legalAddress' rules={validationRule.legalAddress}>
        <Input placeholder={t('common:address')} />
      </Form.Item>

      <Divider orientation='left' orientationMargin='0'>
        {t('form_post_address')}
      </Divider>

      <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
        <Form.Item name='postCountryId' style={{ width: '34%' }} rules={validationRule.postCountryId}>
          <Select
            optionFilterProp='label'
            placeholder={t('common:country')}
            options={transformedCountries}
            showSearch
            allowClear
          />
        </Form.Item>
        <Form.Item name='postPostCode' rules={validationRule.postPostCode} style={{ width: '33%' }}>
          <Input placeholder={t('common:post_code')} />
        </Form.Item>
        <Form.Item name='postCity' rules={validationRule.postCity} style={{ width: '33%' }}>
          <Input placeholder={t('common:city')} />
        </Form.Item>
      </div>
      <Form.Item name='postAddress' rules={validationRule.postAddress}>
        <Input placeholder={t('common:address')} />
      </Form.Item>

      <Form.Item name='emailForDocuments' rules={validationRule.emailForDocuments} style={{ width: '50%' }}>
        <Input placeholder={t('form_email_for_documents')} />
      </Form.Item>

      <Divider orientation='left' orientationMargin='0'>
        {t('form_additional_info')}
      </Divider>

      <Form.Item name='description' rules={validationRule.description}>
        <Input.TextArea rows={5} placeholder={t('common:description')} style={{ resize: 'none' }} />
      </Form.Item>
    </Form>
  );
};
